import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ConversationProvider, useConversation } from '../contexts/ConversationContext';
import { ConversationInterface } from '../components/conversation/ConversationInterface';
import { WorkChatAdapter } from '../adapters/workChatAdapter';
import AdminContextPanel from '../components/admin/AdminContextPanel';
import { useTheme } from '../contexts/ThemeContext';
import { resetWorkChat } from '../services/workChatService';
import { FiTrash2, FiSettings } from 'react-icons/fi';
import ChipManager, { ChatChip, loadChips, saveChips } from '../components/work/ChipManager';

interface ToolCall {
  tool_name: string;
  arguments: string;
  result: string;
  duration_ms: number;
}

const WorkChatInner: React.FC<{
  toolCalls: ToolCall[];
  onReset: () => void;
}> = ({ toolCalls, onReset }) => {
  const { darkMode } = useTheme();
  const { sendMessage, state, resetConversation } = useConversation();
  const [elapsed, setElapsed] = useState(0);
  const [searchParams] = useSearchParams();
  const prefillSent = useRef(false);

  // Auto-send ?prompt= query param once conversation is idle
  useEffect(() => {
    if (prefillSent.current) return;
    const prompt = searchParams.get('prompt');
    if (!prompt || state.status !== 'idle') return;
    prefillSent.current = true;
    window.history.replaceState({}, '', window.location.pathname);
    sendMessage(prompt);
  }, [state.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Timer that shows elapsed seconds while loading
  useEffect(() => {
    if (state.status !== 'loading') { setElapsed(0); return; }
    const interval = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(interval);
  }, [state.status]);

  const [showConfirmClear, setShowConfirmClear] = useState(false);
  const [showChipManager, setShowChipManager] = useState(false);
  const [chips, setChips] = useState<ChatChip[]>(loadChips);
  const confirmRef = useRef<HTMLDivElement>(null);

  const visibleChips = chips.filter(c => !c.hidden);

  const handleFullReset = useCallback(async () => {
    try { await resetWorkChat(); } catch (err) { console.error('Failed to reset work chat:', err); }
    onReset();
    resetConversation();
    setShowConfirmClear(false);
  }, [onReset, resetConversation]);

  // Close confirm popover on outside click
  useEffect(() => {
    if (!showConfirmClear) return;
    const handler = (e: MouseEvent) => {
      if (confirmRef.current && !confirmRef.current.contains(e.target as Node)) setShowConfirmClear(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showConfirmClear]);

  // Chips managed via ChipManager (localStorage-persisted)

  return (
    <div className={`flex h-[900px] max-h-[calc(100vh-200px)] rounded-lg overflow-hidden border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
      {/* Chat panel — 60% */}
      <div className={`w-3/5 flex flex-col border-r ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        {/* Quick actions */}
        <div className={`flex items-center gap-2 px-4 py-2 border-b overflow-x-auto ${darkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
          {visibleChips.map((chip) => (
            <button
              key={chip.id}
              disabled={state.status === 'loading'}
              className={`text-xs px-3 py-1 rounded-full whitespace-nowrap transition-colors ${
                state.status === 'loading' ? 'opacity-50 cursor-not-allowed' : ''
              } ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-white border border-gray-200 hover:bg-gray-100 text-gray-600'}`}
              onClick={() => state.status !== 'loading' && sendMessage(chip.prompt)}
            >
              {chip.label}
            </button>
          ))}
          <button
            onClick={() => setShowChipManager(true)}
            className={`p-1.5 rounded-full shrink-0 transition-colors ${darkMode ? 'text-gray-500 hover:text-gray-300 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
            title="Manage chips"
          >
            <FiSettings className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Chip manager modal */}
        <ChipManager open={showChipManager} onClose={() => setShowChipManager(false)} chips={chips} onChipsChange={setChips} />

        {/* Conversation — loading status embedded in chat flow */}
        <div className="flex-1 overflow-hidden">
          <ConversationInterface
            title="Work Assistant"
            subtitle="AI-powered project planning"
            avatarName="Work Assistant"
            sessionId="work_chat"
            hideHeader={true}
            loadingStatus={
              elapsed < 3 ? 'Thinking...' :
              elapsed < 8 ? 'Analyzing your request...' :
              elapsed < 15 ? 'Running tools and gathering data...' :
              elapsed < 25 ? `Working on it (${elapsed}s)...` :
              `Still working on a multi-step operation (${elapsed}s)...`
            }
          />
        </div>

        {/* Clear conversation — bottom bar */}
        <div className={`relative flex items-center justify-start px-4 py-2 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={() => setShowConfirmClear(true)}
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${
              darkMode ? 'border-gray-700 text-gray-400 hover:text-red-400 hover:border-red-500/40 hover:bg-red-900/20' : 'border-gray-300 text-gray-500 hover:text-red-500 hover:border-red-300 hover:bg-red-50'
            }`}
          >
            <FiTrash2 className="w-3.5 h-3.5" />
            Clear conversation
          </button>

          {/* Confirmation popover */}
          {showConfirmClear && (
            <div
              ref={confirmRef}
              className={`absolute bottom-full left-4 mb-2 p-3 rounded-lg shadow-xl border z-50 ${
                darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}
            >
              <p className={`text-sm mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Clear all messages?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowConfirmClear(false)}
                  className={`text-xs px-3 py-1.5 rounded ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleFullReset}
                  className="text-xs px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700"
                >
                  Yes, clear
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Context panel — 40% (reuses AdminContextPanel with semantic work renderers) */}
      <div className="w-2/5">
        <AdminContextPanel toolCalls={toolCalls} />
      </div>
    </div>
  );
};

const WorkChat: React.FC = () => {
  const { darkMode } = useTheme();
  const [toolCalls, setToolCalls] = useState<ToolCall[]>([]);

  const handleToolCalls = useCallback((newCalls: ToolCall[]) => {
    setToolCalls((prev) => [...prev, ...newCalls].slice(-100));
  }, []);

  const adapter = useMemo(() => new WorkChatAdapter(handleToolCalls), [handleToolCalls]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold bg-gradient-to-r from-emerald-400 to-violet-400 bg-clip-text text-transparent">
          Work Chat
        </h1>
        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Plan, track, and analyze your work with AI
        </p>
      </div>
      <ConversationProvider adapter={adapter} sessionId="work_chat">
        <WorkChatInner toolCalls={toolCalls} onReset={() => setToolCalls([])} />
      </ConversationProvider>
    </div>
  );
};

export default WorkChat;
